import { useEffect } from 'react';
import { SIDE_LABELS } from '../data/inboundBusinessData';

/**
 * 縦ラベル（JAPAN / OSAKA）を画面の高さに合わせて組む。
 *
 * 文字サイズと字間を決め打ちにすると、画面の高さが変わったときに
 * はみ出すか余る。ここでは canvas でフォントの実際の字面（インク）の
 * 高さを測り、「文字が重ならない最小の字送り」を求めたうえで、
 * 文字数ぶんが目標の高さに収まる文字サイズを逆算する。
 *
 * 縦組みの要素幅はフォント指標で決まり事前計算では正確に出せないため、
 * 描画後に実測して gutter を超えていれば縮めて入れ直す。
 */
export default function useSideLabels(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const REF = 200; // 実測用の基準サイズ
    const { heightFill, upRatio, touchGap } = SIDE_LABELS;

    const fit = () => {
      const boxes = Array.from(root.querySelectorAll('[data-sidelabel]'));
      if (boxes.length === 0) return;

      // canvas が使えない環境では測れないので、CSSの既定サイズのままにする。
      // 装飾なので、動かないことより壊れないことを優先する。
      const ctx = document.createElement('canvas').getContext('2d');
      if (!ctx || typeof ctx.measureText !== 'function') return;

      boxes.forEach((box) => {
        const span = box.firstElementChild;
        if (!span) return;
        const text = span.textContent;
        const n = text.length;
        if (n === 0) return;

        const cs = window.getComputedStyle(span);
        ctx.font = `${cs.fontWeight} ${REF}px ${cs.fontFamily}`;

        let ink = 0;
        for (const ch of text) {
          const m = ctx.measureText(ch);
          ink = Math.max(ink, m.actualBoundingBoxAscent + m.actualBoundingBoxDescent);
        }
        const inkPerEm = ink / REF;
        if (!inkPerEm) return;

        // letter-spacing 0 のときの自然な字送りを実測する
        span.style.fontSize = `${REF}px`;
        span.style.letterSpacing = '0px';
        const advPerEm = span.getBoundingClientRect().height / n / REF;

        // 字面が触れない最小の字送り
        const pitchPerEm = inkPerEm * (1 + touchGap);

        const apply = (size) => {
          span.style.fontSize = `${size.toFixed(2)}px`;
          span.style.letterSpacing = `${((pitchPerEm - advPerEm) * size).toFixed(2)}px`;
          return span.getBoundingClientRect();
        };

        let size = Math.max(24, (window.innerHeight * heightFill) / (n * pitchPerEm));
        let rect = apply(size);

        // gutter を超えていれば、その比率で縮めて入れ直す
        for (let i = 0; i < 3 && rect.width > box.clientWidth; i += 1) {
          size = Math.max(24, size * (box.clientWidth / rect.width));
          rect = apply(size);
        }

        // 中央より上へ寄せる。余りが無いときは 0 になるので上端は切れない。
        const free = Math.max(0, window.innerHeight - rect.height);
        span.style.transform = `translateY(${(-(free * upRatio)).toFixed(1)}px)`;
      });
    };

    fit();
    window.addEventListener('resize', fit, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }

    // フッターとの重なりは、フッター側の重ね順で解決している（Footer.js を参照）。
    return () => window.removeEventListener('resize', fit);
  }, [rootRef]);
}
